import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Alert, useTheme, useMediaQuery } from '@mui/material';
import { api } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useGuidesOptional } from '../user_guides/GuidesProvider.jsx';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { FALLBACK_LOCALE } from '../user_guides/i18n/engineStrings';
import { PageHero, FooterLinks, SectionContainer } from '../components/ds';
import { CardSkeleton, BalanceSkeleton } from '../components/SkeletonLoader';
import { CenteredLoader } from '../components/LoadingIndicator';
import { useToast } from '../components/ToastNotification';
import { SectionErrorBoundary } from '../components/ErrorBoundary';
import { DashboardLedgerGroups, DashboardBudgetGroups, GroupSectionLabel } from '../components/DashboardGroups';
import NetHistoryBars from '../components/NetHistoryBars';
import MonthSwitcher from '../components/dashboard/MonthSwitcher';
import BottomLine from '../components/dashboard/BottomLine';
import MoneyInOut from '../components/dashboard/MoneyInOut';
import BudgetsByCategory from '../components/dashboard/BudgetsByCategory';
import { subscribeTangibleRefresh } from '../utils/tangibleRefresh';
import { localYearMonth, monthLabel } from '../utils/costOfLiving';
import { checkingCents, switcherMonths } from '../utils/dashboardN';
import { balancesFromTangible } from '../utils/tangibleBalances';
import { interpolate } from '../utils/entryType';

// The chart covers the 24 full months before the current one, then the
// current month so far; the call asks for 25.
const HISTORY_MONTHS = 24;

/**
 * Dashboard_N (#2139, epic #2138): the Dashboard that explains each month in
 * plain words. Chosen per user by the "Dashboard layout" preference; the
 * classic Dashboard stays the default and is untouched. Top to bottom: the
 * page hero with the payday aux, the Month view card (switcher, Bottom line,
 * Money in and out, the Net by month chart), the Budgets by Category card
 * (#2143, from `/budget-months` and the `/tangible` `byBudget` rows), then
 * the Ledgers and Budgets groups open on arrival.
 *
 * Data: `/flows` (25 months) and `/tangible` on mount and on every tangible
 * refresh signal; `/ledgers`, `/budgets` and `/next-payday` as the classic
 * Dashboard loads them. The groups' balances come from that same `/tangible`
 * response (#2158), so no per-entity `/balance` call is made.
 */
const DashboardN = ({ isAuthReady = false }) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const { activeGroupId } = useGroup();
  const strings = useEngineStrings();
  const locale = useGuidesOptional()?.language || FALLBACK_LOCALE;
  const { showError } = useToast();
  const [loading, setLoading] = useState({ lists: true, payday: true, month: true });
  const [error, setError] = useState(null);
  const [monthError, setMonthError] = useState(false);
  const [data, setData] = useState({
    ledgers: [],
    budgets: [],
    budgetCategories: [],
    nextPayday: null,
  });
  const [flows, setFlows] = useState([]);
  const [tangible, setTangible] = useState(null);
  // #2143: the category table's month rows, null until loaded; they fail on
  // their own so a 404 from an older backend touches that card alone.
  const [budgetMonths, setBudgetMonths] = useState(null);
  const [budgetMonthsError, setBudgetMonthsError] = useState(false);
  // #2144: the chart's Raw caption names a month's outlier payees, fetched
  // the first time that month is shown on Raw and kept for the page's life;
  // a failed call stores an empty list so nothing shows and nothing retries.
  const [outlierPayees, setOutlierPayees] = useState({});
  const outlierRequests = useRef(new Set());
  const needOutlierPayees = useCallback((yearMonth) => {
    if (!activeGroupId || outlierRequests.current.has(yearMonth)) return;
    outlierRequests.current.add(yearMonth);
    api.get(activeGroupId, `/outlier-payees?yearMonth=${encodeURIComponent(yearMonth)}`)
      .then((r) => setOutlierPayees((prev) => ({ ...prev, [yearMonth]: r?.payees || [] })))
      .catch((err) => {
        console.error('Failed to load outlier payees:', err);
        setOutlierPayees((prev) => ({ ...prev, [yearMonth]: [] }));
      });
  }, [activeGroupId]);
  const currentYearMonth = localYearMonth();
  // The Month view's month, the Money in and out tab, the chart's series and
  // the Link chip all start fresh on every mount (epic decisions 7 and 8).
  const [selected, setSelected] = useState(currentYearMonth);
  const [tab, setTab] = useState('budgeted');
  const [series, setSeries] = useState('baseline');
  const [linked, setLinked] = useState(false);

  const loadDashboard = useCallback(async () => {
    if (!activeGroupId) return;
    try {
      setError(null);
      const [ledgersRes, budgetsRes, paydayRes] = await Promise.all([
        api.get(activeGroupId, '/ledgers'),
        api.get(activeGroupId, '/budgets'),
        api.get(activeGroupId, '/next-payday').catch(() => ({ nextPayday: null }))
      ]);
      const ledgers = (ledgersRes.ledgers || []).filter(l => !l.archived);
      const budgets = (budgetsRes.budgets || []).filter(b => !b.archived);
      setData(prev => ({
        ...prev,
        ledgers,
        budgets,
        budgetCategories: budgetsRes.budgetCategories || [],
        nextPayday: paydayRes?.nextPayday || null
      }));
      setLoading(prev => ({ ...prev, lists: false, payday: false }));
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      const msg = err.message || 'Failed to load dashboard.';
      setError(msg);
      showError('Dashboard load failed: ' + msg);
      setLoading(prev => ({ ...prev, lists: false, payday: false }));
    }
  }, [activeGroupId, showError]);

  // The groups' balance map, in dollars under the `l:` and `b:` keys the
  // group components read, from the `/tangible` response already loaded.
  const balances = useMemo(() => {
    const { ledgers: ledgerCents, budgets: budgetCents } = balancesFromTangible(tangible);
    const map = {};
    data.ledgers.forEach(l => { map[`l:${l.ledgerId}`] = (ledgerCents[l.ledgerId] ?? 0) / 100; });
    data.budgets.forEach(b => { map[`b:${b.budgetId}`] = (budgetCents[b.budgetId] ?? 0) / 100; });
    return map;
  }, [tangible, data.ledgers, data.budgets]);
  const balancesLoading = tangible === null;

  useEffect(() => {
    if (isAuthReady && activeGroupId) loadDashboard();
  }, [isAuthReady, activeGroupId, loadDashboard]);

  // Month buckets and the balance cache are stream-maintained, so both follow
  // the header's refresh signal and its backoff, as the header does.
  useEffect(() => {
    if (!isAuthReady || !activeGroupId) return undefined;
    let cancelled = false;
    const load = async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(prev => ({ ...prev, month: true }));
        const [flowsRes, tangibleRes, monthsRes] = await Promise.all([
          api.get(activeGroupId, `/flows?months=${HISTORY_MONTHS + 1}&asOf=${encodeURIComponent(localYearMonth())}`),
          api.get(activeGroupId, '/tangible'),
          api.get(activeGroupId, `/budget-months?yearMonth=${encodeURIComponent(localYearMonth())}`)
            .then((r) => ({ rows: r?.budgets || [] }))
            .catch((err) => {
              console.error('Failed to load budget months:', err);
              return { failed: true };
            }),
        ]);
        if (!cancelled) {
          setFlows(flowsRes?.months || []);
          setTangible(tangibleRes || {});
          setMonthError(false);
          if (!monthsRes.failed) {
            setBudgetMonths(monthsRes.rows);
            setBudgetMonthsError(false);
          } else if (!silent) {
            setBudgetMonthsError(true);
          }
        }
      } catch (err) {
        console.error('Failed to load month view:', err);
        // A silent reload keeps the last good month on screen.
        if (!cancelled && !silent) setMonthError(true);
      } finally {
        if (!cancelled && !silent) setLoading(prev => ({ ...prev, month: false }));
      }
    };
    load();
    const unsubscribe = subscribeTangibleRefresh(() => load({ silent: true }));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [isAuthReady, activeGroupId]);

  if (!isAuthReady) return <CenteredLoader message="Initializing..." />;

  if (loading.lists) {
    return (
      <Box sx={{ pb: 8 }}>
        <PageHero label="Dashboard" />
        <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
          <CardSkeleton count={2} />
        </Box>
      </Box>
    );
  }

  const ledgerCount = data.ledgers.length;
  const budgetCount = data.budgets.length;
  const groupDensity = isWide ? 'default' : 'compact';
  const rowSize = isWide ? 'compact' : 'default';

  const paydayAux = (
    <SectionErrorBoundary title="Payday Information Error">
      <Box sx={{ textAlign: 'right' }}>
        <Typography component="div" sx={{ fontSize: '11px', color: palette.text.dim }}>
          Next payday
        </Typography>
        {loading.payday ? (
          <Box sx={{ mt: '4px', display: 'flex', justifyContent: 'flex-end' }}>
            <BalanceSkeleton width="80px" />
          </Box>
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: '14px',
              fontVariantNumeric: 'tabular-nums',
              color: data.nextPayday ? palette.interactive : palette.text.dim,
              marginTop: '2px',
            }}
          >
            {data.nextPayday || 'Unavailable'}
          </Typography>
        )}
      </Box>
    </SectionErrorBoundary>
  );

  const dimNote = (text, italic = false) => (
    <Typography
      sx={{
        fontSize: `${ds.font.row}px`,
        color: palette.text.dim,
        fontStyle: italic ? 'italic' : 'normal',
        paddingTop: `${ds.space[2]}px`,
        paddingBottom: `${ds.space[2]}px`,
      }}
    >
      {text}
    </Typography>
  );

  // Month view body: the switcher's months, the shown month's entry, and the
  // figures from /tangible.
  const months = switcherMonths(flows, currentYearMonth);
  const shown = flows.find((m) => m.yearMonth === selected) || flows[flows.length - 1] || null;
  const isCurrent = Boolean(shown) && shown.yearMonth === currentYearMonth;
  const previous = flows.length > 1 ? flows[flows.length - 2] : null;
  const divider = { borderTop: `1px solid ${palette.divider}`, pt: `${ds.space[4]}px`, mt: `${ds.space[2]}px` };

  let monthBody;
  if (loading.month) {
    monthBody = (
      <Box sx={{ py: `${ds.space[5]}px` }}>
        <BalanceSkeleton width="180px" />
      </Box>
    );
  } else if (monthError) {
    monthBody = dimNote(strings['dashboardN.error']);
  } else if (!shown) {
    monthBody = dimNote(interpolate(strings['dashboardN.empty'], { month: monthLabel(currentYearMonth, locale) }), true);
  } else {
    // The card body (#2152): from md up the board's two columns, the switcher
    // and the Bottom line on the left and Money in and out on the right,
    // equal halves (#2163); below md one column with a divider between them.
    const emptyMonth = !shown.lineCount && !isCurrent;
    const switcher = (
      <Box sx={{ display: 'flex', justifyContent: isWide ? 'flex-start' : 'flex-end', pt: `${ds.space[3]}px` }}>
        <MonthSwitcher
          months={months}
          value={shown.yearMonth}
          onChange={setSelected}
          linked={linked}
          strings={strings}
          locale={locale}
        />
      </Box>
    );
    const bottomLine = emptyMonth
      ? dimNote(interpolate(strings['dashboardN.empty'], { month: monthLabel(shown.yearMonth, locale) }), true)
      : (
        <BottomLine
          month={shown}
          isCurrent={isCurrent}
          positiveBudgetCents={tangible?.positiveBudgetCents || 0}
          checkingCents={checkingCents(tangible?.byAccount)}
          strings={strings}
        />
      );
    const moneyInOut = emptyMonth ? null : (
      <MoneyInOut
        month={shown}
        isCurrent={isCurrent}
        positiveBudgetCents={tangible?.positiveBudgetCents || 0}
        tab={tab}
        onTabChange={setTab}
        strings={strings}
      />
    );
    monthBody = (
      <>
        {isWide ? (
          <Box data-testid="month-view-body" data-layout="two-column" sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
            <Box sx={{ minWidth: 0, pr: `${ds.space[8]}px`, borderRight: `1px solid ${palette.divider}`, display: 'flex', flexDirection: 'column' }}>
              {switcher}
              {/* #2194: the figures sit centered in the room left beside Money in and out. */}
              <Box data-testid="bottom-line-well" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {bottomLine}
              </Box>
            </Box>
            <Box sx={{ minWidth: 0, pl: `${ds.space[8]}px`, pt: `${ds.space[3]}px` }}>
              {moneyInOut}
            </Box>
          </Box>
        ) : (
          <Box data-testid="month-view-body" data-layout="stacked">
            {switcher}
            {bottomLine}
            {moneyInOut && <Box sx={divider}>{moneyInOut}</Box>}
          </Box>
        )}
        <Box sx={{ ...divider, pb: `${ds.space[5]}px` }}>
          <NetHistoryBars
            months={flows}
            anchorYearMonth={previous?.yearMonth}
            currentYearMonth={currentYearMonth}
            strings={strings}
            locale={locale}
            series={series}
            onSeriesChange={setSeries}
            linked={linked}
            onLinkedChange={setLinked}
            linkedYearMonth={shown.yearMonth}
            onPickMonth={setSelected}
            outlierPayees={outlierPayees}
            onNeedOutlierPayees={needOutlierPayees}
          />
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ pb: 8 }} data-testid="dashboard-n">
      <PageHero label="Dashboard" aux={paydayAux} />

      {error && (
        <Alert severity="error" sx={{ mt: 3, mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        <SectionErrorBoundary title="Month View Error">
          <SectionContainer
            title={strings['dashboardN.title']}
            showDot={false}
            density={isWide ? 'default' : 'compact'}
            aria-label={strings['dashboardN.title']}
            data-testid="month-view"
          >
            {monthBody}
          </SectionContainer>
        </SectionErrorBoundary>
      </Box>

      <Box sx={{ mt: 3 }} data-testid="budgets-by-category-slot">
        <SectionErrorBoundary title="Budgets by Category Error">
          <BudgetsByCategory
            budgets={data.budgets}
            budgetCategories={data.budgetCategories}
            monthRows={loading.month ? null : budgetMonths}
            error={budgetMonthsError}
            byBudget={tangible?.byBudget || []}
            unbudgetedSpendingCents={flows.find((m) => m.yearMonth === currentYearMonth)?.unbudgetedSpendingCents || 0}
            yearMonth={currentYearMonth}
            strings={strings}
            locale={locale}
            density={isWide ? 'default' : 'compact'}
          />
        </SectionErrorBoundary>
      </Box>

      <Box component="section" sx={{ mt: 3 }}>
        <GroupSectionLabel>Ledgers</GroupSectionLabel>
        <SectionErrorBoundary title="Ledger Error">
          {ledgerCount === 0
            ? dimNote('No ledgers.')
            : (
              <DashboardLedgerGroups
                ledgers={data.ledgers}
                balances={balances}
                balancesLoading={balancesLoading}
                density={groupDensity}
                rowSize={rowSize}
                defaultCollapsed={false}
              />
            )}
        </SectionErrorBoundary>
      </Box>

      <Box component="section" sx={{ mt: 3 }}>
        <GroupSectionLabel>Budgets</GroupSectionLabel>
        <SectionErrorBoundary title="Budget Error">
          {budgetCount === 0
            ? dimNote('No budgets.')
            : (
              <DashboardBudgetGroups
                budgets={data.budgets}
                budgetCategories={data.budgetCategories}
                balances={balances}
                balancesLoading={balancesLoading}
                density={groupDensity}
                rowSize={rowSize}
                defaultCollapsed={false}
                onBudgetUpdated={loadDashboard}
              />
            )}
        </SectionErrorBoundary>
      </Box>

      <FooterLinks sx={{ mt: 3 }} />
    </Box>
  );
};

export default DashboardN;
